import _ from 'lodash';

// Only include styles that SVG actually uses.  Additional unused ones can cause errors in jsPDF.
// Add new styles here as needed, but test in all browsers because it could break jsPDF in some browsers.
const defaultStyles = ['fill', 'stroke', 'stroke-width', 'font-size', 'font-weight', 'font-style', 'font-family', 'color', 'opacity', 'fill-opacity', 'stroke-opacity', 'alignment-baseline', 'text-anchor'];

function getRGB(str) {
    const regex = str.startsWith('rgba') ? /rgba\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)/ : /rgb\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)/;
    const match = str.match(regex);
    // eslint-disable-next-line
    if (match[4] == '0') {
        return str;
    }
    const hexes = _.map(match, (m) => {
        if (!m) {
            return '00';
        }
        const hex = parseInt(m, 10).toString(16);
        if (hex.length === 1) {
            return `0${hex}`;
        }
        return hex;
    });
    return match ? `#${hexes[1]}${hexes[2]}${hexes[3]}` : str;
}

/**
 * Get the computed styles of the specified element and return as a string of concatenated CSS
 * rules.
 * @param {Node} element - The element to compute styling information from.
 * @param {String[]} svgCSSStyles - CSS styles to include in the style string.
 * @returns {string}
 */
function getComputedStyleString(element, svgCSSStyles) {
    const computedStyle = window.getComputedStyle(element);
    let computedStyleStr = '';
    for (let i = 0; i < computedStyle.length; i++) {
        const key = computedStyle[i];
        if (svgCSSStyles.includes(key)) {
            let value = computedStyle.getPropertyValue(key);
            if (key === 'stroke' && value.startsWith('rgb')) {
                value = getRGB(value);
            }
            computedStyleStr += `${key}:${value};`;
            // if (value !== 'none') {

            // }
        }
    }
    return computedStyleStr;
}

/**
 * Recursively visits all child nodes of the specified SVG element and returns them as an array.
 * @param {Node} svg - The SVG element to traverse the children of.
 * @returns {Node[]}
 */
function traverse(svg) {
    const defaultExcludedTags = ['SCRIPT', 'defs', 'clipPath'];
    const tree = [svg]; // Add the SVG itself as the first element in the array
    function visit(node) {
        if (node && node.hasChildNodes()) {
            let child = node.firstChild;
            while (child) {
                if (child.nodeType === 1 && !defaultExcludedTags.includes(child.nodeName)) {
                    tree.push(child);
                    visit(child);
                }
                child = child.nextSibling;
            }
        }
    }
    visit(svg);
    // return [svg];
    return tree;
}

/**
 * Convert all computed styles of an SVG element into inline styles.
 * @param {SVGElement} svg - The SVG element to generate inline styling for/from.
 * @param {boolean} inPlace - Whether to modify the SVG element in place or return a clone.
 * @param {String[]} excludedKeys - CSS keys to exclude from the inline style string.
 * @returns {Node | null}
 */
export function inlineSvg(svg, inPlace = false, excludedKeys = []) {
    const svgCSSStyles = excludedKeys ? _.xor(defaultStyles, excludedKeys) : defaultStyles;
    const svgElements = traverse(svg);
    if (inPlace) {
        for (let i = 0; i < svgElements.length; i++) {
            svgElements[i].setAttribute(
                'style',
                getComputedStyleString(svgElements[i], svgCSSStyles)
            );
        }
        return null;
    }

    const clonedSvg = svg.cloneNode(true);
    const clonedElements = traverse(clonedSvg);
    for (let i = 0; i < svgElements.length; i++) {
        clonedElements[i].setAttribute(
            'style',
            getComputedStyleString(svgElements[i], svgCSSStyles)
        );
    }
    return clonedSvg;
}

/**
 * Temporarily attach an unattached SVG element to the DOM in order to add inline styles based on
 * computed style properties. Once styled, the element is detached from the DOM.
 * @param {SVGElement} unattachedSvg - An SVG element that is not attached to the DOM.
 * @param {SVGElement} srcElement - The element that the unattached SVG was cloned from (if any).
 * @param {String[]} excludedKeys - CSS keys to exclude from the inline style string.
 * @returns {Node}
 */
export function inlineSvgUnattached(unattachedSvg, srcElement = null, excludedKeys = []) {
    const svgCSSStyles = excludedKeys ? _.xor(defaultStyles, excludedKeys) : defaultStyles;
    if (srcElement) {
        const srcElements = traverse(srcElement);
        const clonedElements = traverse(unattachedSvg);
        for (let i = 0; i < srcElements.length; i++) {
            clonedElements[i].setAttribute(
                'style',
                getComputedStyleString(srcElements[i], svgCSSStyles)
            );
        }
        return unattachedSvg;
    }

    // eslint-disable-next-line no-param-reassign
    unattachedSvg.style.display = 'none';
    document.body.appendChild(unattachedSvg);
    const clonedElements = traverse(unattachedSvg);
    for (let i = 0; i < clonedElements.length; i++) {
        clonedElements[i].setAttribute(
            'style',
            getComputedStyleString(clonedElements[i], svgCSSStyles)
        );
    }
    document.body.removeChild(unattachedSvg);
    return unattachedSvg;
}
